import {useEffect, useRef, useState} from "react";
import {ChevronDown, Loader2, Plus} from "lucide-react";

import {Button} from "@/components/ui/button";
import {Sheet, SheetContent, SheetHeader, SheetTitle} from "@/components/ui/sheet";
import {Loader, ListInfiniteLoadMore, ListInfiniteEndBlock} from "@/src/modules/core/components";

import {ResidentHomeSafelistingsQuotaSummary} from "@/src/modules/home/components/resident-home-safelistings-quota-summary";
import {RESIDENT_SAFELISTING_RESTRICTION_MESSAGE} from "@/src/modules/home/lib/resident-amenity-restriction-copy";
import {useInfiniteSafelistings} from "@/src/modules/safelistings/hooks/use-infinite-safelistings";
import {safelistingsService} from "@/src/modules/safelistings/services";
import type {ResidentHomeSafelistingsAccountability} from "@/src/modules/safelistings/types/resident-home-accountability";

import {ResidentHomeSafelistingsPreviewRow} from "./resident-home-safelistings-preview-row";
import {ResidentAmenityRestrictionNotice} from "./resident-amenity-restriction-notice";

type ResidentHomeSafelistingsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filter: Record<string, unknown>;
  householdId: number | null;
  accountability: ResidentHomeSafelistingsAccountability | null;
  isQuotaPending?: boolean;
  isCreateBlocked: boolean;
  isSafelistingAmenityBlocked?: boolean;
  newButtonTitle?: string;
  hideCreate?: boolean;
  onNew: () => void;
  onSelectDetail: (id: number) => void;
};

export function ResidentHomeSafelistingsSheet({
  open,
  onOpenChange,
  filter,
  householdId,
  accountability,
  isQuotaPending = false,
  isCreateBlocked,
  isSafelistingAmenityBlocked = false,
  newButtonTitle,
  hideCreate = false,
  onNew,
  onSelectDetail,
}: ResidentHomeSafelistingsSheetProps) {
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const hasMoreRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const userEngagedRef = useRef(false);

  const [showScrollHint, setShowScrollHint] = useState(false);
  const [needsManualLoad, setNeedsManualLoad] = useState(false);

  const canCreate = safelistingsService.getIsCreateEnabled();
  const isDetailEnabled = safelistingsService.getIsShowEnabled();

  const {
    safelistings,
    totalRecords,
    isPending,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteSafelistings({
    filter,
    sort: "-start_date",
    enabled: open && Boolean(householdId),
  });

  const hasMore = hasNextPage ?? false;
  const isLoadingMore = isFetchingNextPage ?? false;

  hasMoreRef.current = hasMore;
  isLoadingMoreRef.current = isLoadingMore;

  useEffect(() => {
    if (!open) {
      userEngagedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    const el = scrollRootRef.current;
    if (!el || !open) return;

    const updateChrome = () => {
      if (el.scrollTop > 0) {
        userEngagedRef.current = true;
      }
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const atBottom = distanceFromBottom < 24;
      const hasOverflow = el.scrollHeight > el.clientHeight + 1;
      setShowScrollHint(hasOverflow && !atBottom && hasMoreRef.current);
      setNeedsManualLoad(hasMoreRef.current && !hasOverflow);
    };

    updateChrome();
    el.addEventListener("scroll", updateChrome, {passive: true});
    const resizeObserver = new ResizeObserver(updateChrome);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateChrome);
      resizeObserver.disconnect();
    };
  }, [open, safelistings.length, isPending, hasMore]);

  useEffect(() => {
    const root = scrollRootRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !open) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (!hasMoreRef.current || isLoadingMoreRef.current) return;
        if (!userEngagedRef.current) return;

        const hasOverflow = root.scrollHeight > root.clientHeight + 1;
        if (!hasOverflow) return;

        void fetchNextPage();
      },
      {root, threshold: 0, rootMargin: "80px"},
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, safelistings.length, fetchNextPage]);

  const handleNew = () => {
    onOpenChange(false);
    onNew();
  };

  const handleSelect = (id: number) => {
    if (!isDetailEnabled) return;
    onSelectDetail(id);
  };

  const handleManualLoad = () => {
    userEngagedRef.current = true;
    void fetchNextPage();
  };

  const loadMoreBlock = (
    <div className="flex min-h-12 flex-col items-center justify-center gap-2 py-4">
      {isLoadingMore ? (
        <ListInfiniteLoadMore />
      ) : !hasMore && !isPending ? (
        <ListInfiniteEndBlock totalRecords={totalRecords} />
      ) : hasMore && needsManualLoad ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full max-w-xs"
          disabled={isLoadingMore}
          onClick={handleManualLoad}
        >
          Load more
        </Button>
      ) : null}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex h-full max-h-[100dvh] min-h-0 flex-col gap-0 overflow-hidden overflow-y-hidden p-0 sm:max-w-md"
      >
        <SheetHeader className="border-border shrink-0 border-b">
          <div className="flex flex-col gap-2 pr-8">
            <div className="flex items-center justify-between gap-2">
              <SheetTitle>Safelistings</SheetTitle>
              {canCreate && !hideCreate ? (
                <Button
                  type="button"
                  size="sm"
                  disabled={isCreateBlocked}
                  title={newButtonTitle}
                  onClick={handleNew}
                >
                  <Plus className="size-4" aria-hidden />
                  New
                </Button>
              ) : null}
            </div>
            {householdId ? (
              <ResidentHomeSafelistingsQuotaSummary
                accountability={accountability}
                isPending={isQuotaPending}
              />
            ) : null}
            {isSafelistingAmenityBlocked ? (
              <ResidentAmenityRestrictionNotice message={RESIDENT_SAFELISTING_RESTRICTION_MESSAGE} />
            ) : null}
          </div>
        </SheetHeader>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          <div
            ref={scrollRootRef}
            className="h-full min-h-0 overflow-y-auto overscroll-contain px-4 py-4 [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-track]:bg-transparent"
          >
            {!householdId ? (
              <p className="text-muted-foreground text-sm">No household linked.</p>
            ) : isPending ? (
              <Loader className="min-h-48" />
            ) : safelistings.length === 0 ? (
              <p className="text-muted-foreground text-sm">No safelistings yet.</p>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  {safelistings.map((row) => (
                    <ResidentHomeSafelistingsPreviewRow
                      key={row.safelisting_id}
                      safelisting={row}
                      amenityRestricted={isSafelistingAmenityBlocked}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
                {loadMoreBlock}
                <div ref={sentinelRef} className="h-px w-full shrink-0" aria-hidden />
              </>
            )}
          </div>

          {showScrollHint ? (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center gap-1 bg-gradient-to-t from-background from-40% to-transparent px-4 pb-3 pt-10"
              aria-hidden
            >
              <ChevronDown className="text-muted-foreground size-4 motion-safe:animate-bounce" />
              <span className="text-muted-foreground text-xs font-medium">Scroll for more</span>
            </div>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
